import { useEnhancedRouter } from '@/hooks/useEnhancedRouter';
import axiosPrivate from '@/services/axiosPrivate';
import { fetchData } from '@/services/fetchData';
import { selectUserData, setUserData } from '@/store';
import { BackendBookingDateType, EventType, FETCH_METHODS } from '@/types';
import { formatPhone } from '@/utils/formatPhone';
import { getLocaleDate } from '@/utils/getLocaleDate';
import { isEventFinishedToday } from '@/utils/isEventFinishedToday';
import { useEffect, useState } from 'react';
import {
  SubmitHandler,
  useFieldArray,
  useForm,
  useWatch,
} from 'react-hook-form';
import toast from 'react-hot-toast';
import { useDispatch, useSelector } from 'react-redux';

export type PersonInput = {
  name: string;
  surname: string;
  phone: string;
  email: string;
};

export type EventRegistrationInputs = {
  persons: PersonInput[];
  agreeToProcessingPersonalData: boolean;
  agreeToRules: boolean;
};

type UseOneEventRegistrationProps = {
  events: EventType[];
  bookingDates: BackendBookingDateType[];
  parentEvent: string;
  locale?: string;
  common: Record<string, string>;
};

export const useOneEventRegistration = ({
  events,
  bookingDates,
  parentEvent,
  locale,
  common,
}: UseOneEventRegistrationProps) => {
  const user = useSelector(selectUserData);
  const dispatch = useDispatch();
  const { push } = useEnhancedRouter();
  const [formData, setFormData] = useState({
    event: events[0].code,
    date: null as Date | null,
    time: {
      timeFrom: events[0].timeFrom,
      timeTo: events[0].timeTo,
    },
  });
  const [buttonDisabled, setButtonDisabled] = useState(true);
  const [availableSeats, setAvailableSeats] = useState(0);
  const [noSeatsPopup, setNoSeatsPopup] = useState('');
  const [policyLink, setPolicyLink] = useState('');
  const [rulesLink, setRulesLink] = useState('');

  const getDefaultPersonValues = (userData: typeof user): PersonInput => ({
    name: userData?.name || '',
    surname: userData?.lastName || '',
    phone: formatPhone(userData?.phone || '') || '+7',
    email: userData?.email || '',
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    control,
  } = useForm<EventRegistrationInputs>({
    defaultValues: {
      persons: [getDefaultPersonValues(user)],
      agreeToProcessingPersonalData: false,
      agreeToRules: true,
    },
  });

  const formFields = useWatch({ control });

  useEffect(() => {
    if (user) {
      setValue('persons', [
        {
          name: user.name,
          surname: user.lastName,
          phone: formatPhone(user.phone),
          email: user.email,
        },
      ]);
    } else {
      const getUser = async () => {
        const res = await axiosPrivate.get('/user');
        if (!res) return;
        if (res.data) {
          dispatch(setUserData(res.data.user));
        }
      };
      getUser();
    }
  }, [user]);

  useEffect(() => {
    const fetchPolicyLink = async () => {
      const response = await fetchData({
        url: `/settings/view`,
        locale,
      });
      setPolicyLink(response.linkProcessingPersonalData || '/faq');
      setRulesLink(response.linkRulesVisiting || '/faq');
    };
    fetchPolicyLink();
  }, []);

  // Выбор минимальной даты события (даже если мест нет)
  useEffect(() => {
    const currentEventId = events[0].id;
    const filteredDatesByEvent = bookingDates.filter(
      (item) => item.parentId === currentEventId,
    );
    if (filteredDatesByEvent.length > 0) {
      const sorted = filteredDatesByEvent
        .slice()
        .sort(
          (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime(),
        );
      setFormData((prev) => ({
        ...prev,
        date: new Date(sorted[0].date),
      }));
      setAvailableSeats(sorted[0].seats);
    } else {
      setFormData((prev) => ({ ...prev, date: null }));
      setAvailableSeats(0);
    }
  }, [events, bookingDates]);

  // Проверка доступности мест при смене даты (без показа попапа)
  const handleDateChange = (value: Date) => {
    const currentEventId = events[0].id;
    const found = bookingDates.find(
      (item) =>
        item.parentId === currentEventId &&
        new Date(item.date).toLocaleDateString('ru-RU') ===
          value.toLocaleDateString('ru-RU'),
    );
    setFormData((prev) => ({ ...prev, date: value }));
    setAvailableSeats(found?.seats ?? 0);
    setNoSeatsPopup('');
  };

  const handleClosePopup = () => {
    setNoSeatsPopup('');
  };

  // Кнопка disabled если нет согласия, нет мест, не заполнены поля и т.д.
  useEffect(() => {
    const agree =
      formFields.agreeToProcessingPersonalData && formFields.agreeToRules;
    const hasSeats = availableSeats > 0;
    const allFieldsFilled =
      Array.isArray(formFields.persons) &&
      formFields.persons.length > 0 &&
      !!formFields.persons[0].name?.trim() &&
      !!formFields.persons[0].surname?.trim() &&
      !!formFields.persons[0].phone?.trim() &&
      !!formFields.persons[0].email?.trim() &&
      formFields.persons.slice(1).every((p) => !!p.name?.trim());
    setButtonDisabled(
      !agree ||
        !hasSeats ||
        !formData.date ||
        !allFieldsFilled ||
        isEventFinishedToday(events[0], formData.date),
    );
  }, [formFields, availableSeats, formData.date, events]);

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'persons',
  });

  const onSubmit: SubmitHandler<EventRegistrationInputs> = async (data) => {
    if (buttonDisabled) return;

    setButtonDisabled(true);

    const response = await fetchData({
      url: `/content/event/${parentEvent}/registration`,
      method: FETCH_METHODS.POST,
      body: {
        ...data,
        date: formData.date?.toLocaleString('ru-RU'),
        event: formData.event,
      },
    });
    if (response && response.code === 200) {
      push(
        `/success?date=${encodeURIComponent(getLocaleDate(formData.date || new Date(), locale))}&timeFrom=${encodeURIComponent(formData.time.timeFrom)}&timeTo=${encodeURIComponent(formData.time.timeTo)}&event=events`,
      );
    } else if (response?.data?.errorCode === 'event_no_seats') {
      const eventName = response?.data?.info?.[0]?.event?.name || '';
      setNoSeatsPopup(eventName);
    } else {
      toast.error(common.error);
    }
    setButtonDisabled(false);
  };

  return {
    formData,
    setFormData,
    availableSeats,
    noSeatsPopup,
    handleClosePopup,
    handleDateChange,
    buttonDisabled,
    policyLink,
    rulesLink,
    register,
    handleSubmit,
    errors,
    setValue,
    control,
    fields,
    append,
    remove,
    onSubmit,
  };
};
